import { useMutation } from "@tanstack/react-query";
import { replaceExercise } from "api-client";
import { assert } from "utils";

import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";

import { useWorkoutItemMutationScope } from "./useWorkoutItemMutationScope";
import { useWorkoutEditStore } from "./WorkoutEditContext";

interface ReplaceExerciseInput {
  workoutItemId: string;
  exerciseId: string;
}

export function useReplaceExercise(workoutItemId: string) {
  const workoutEditStore = useWorkoutEditStore();
  const { startSaving } = useAuthenticatedNavigationContext();
  const scope = useWorkoutItemMutationScope(workoutItemId);

  const mutation = useMutation({
    scope,
    mutationFn: async (input: ReplaceExerciseInput) => {
      const response = await replaceExercise({
        path: {
          workoutId: workoutEditStore.workout.id,
          workoutItemId: input.workoutItemId,
        },
        body: { exerciseId: input.exerciseId },
      });
      return response.data.data.workoutItem;
    },
    // Here, not in mutate's callbacks: those run only for the latest call.
    onSuccess: (updatedWorkoutItem, input) => {
      assert(updatedWorkoutItem.exercise, "Replaced item has no exercise");
      workoutEditStore.updateWorkoutItem(
        input.workoutItemId,
        updatedWorkoutItem,
      );
    },
  });

  // Resolves after the save, so the exercise combobox shows it loading.
  async function replace(input: ReplaceExerciseInput) {
    const stopSaving = startSaving();

    try {
      await mutation.mutateAsync(input);
      stopSaving();
    } catch {
      stopSaving("error");
    }
  }

  return replace;
}
